import { Injectable, OnDestroy } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Actions } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Subject } from 'rxjs';
import { FormsBase } from 'src/app/common-components/forms-base/forms-base';
import { trimPhoneNumber } from 'src/app/common-components/helpers/trim-phone-number-helper';
import { phoneNumberValidator } from 'src/app/common-components/validators/phone-number.validator';
import { PromptDetailForm, PromptDetailFormValue } from '../models/prompt-detail-form';
import { PromptDetailSaveModel } from '../models/prompt-detail-save.model';
import { PromptDetailView } from '../models/prompt-detail-view.model';

@Injectable({
   providedIn: 'root',
})
export class PromptDetailFormService extends FormsBase implements OnDestroy {
   private destroyed$ = new Subject<void>();

   constructor(fb: UntypedFormBuilder, private store: Store, private action: Actions) {
      super();
      const form: PromptDetailForm = {
         account: fb.control(null),
         createDate: fb.control(null),
         createOptionControl: fb.control(null),
         createUser: fb.control(null),
         destinationExtension: fb.control(null),
         destinationNumber: fb.control(null, phoneNumberValidator()),
         file: fb.control(null),
         fileAttachment: fb.control(null),
         fileId: fb.control(null),
         isActive: fb.control(null),
         isReusable: fb.control(null),
         name: fb.control(null, [Validators.pattern(/^\s*(?!\s*$).+/)]),
         promptId: fb.control(null),
         typeId: fb.control(null),
         updateDate: fb.control(null),
         updateUser: fb.control(null),
      };

      this.form = fb.group(form);
   }

   ngOnDestroy(): void {
      this.destroyed$.next();
   }

   updatePrompt(prompt: PromptDetailView): void {
      if (!prompt) {
         return;
      }

      const formValue: Partial<PromptDetailFormValue> = {
         account: prompt.account,
         file: prompt.file,
         fileAttachment: null,
         fileId: prompt.file?.fileId,
         isActive: prompt.isActive,
         isReusable: prompt.isReusable,
         name: prompt.name,
         promptId: prompt.promptId,
         typeId: { typeName: prompt.typeId },
      };
      this.form.patchValue(formValue);

      this.markAsUntouchedAndPristine();
   }

   getValue(promptId: number | null): PromptDetailSaveModel {
      const value = this.form.value as PromptDetailFormValue;
      return {
         accountId: value.account?.accountId ?? null,
         file: null,
         fileId: value.file?.fileId ?? null,
         isActive: value.isActive,
         isReusable: value.isReusable,
         name: value.name,
         promptId: promptId,
         typeId: value.typeId?.typeName ?? null,
         dontDeleteFileOnUpdate: null,
         copyMohFile: null,
      };
   }

   getDestinationExtension(): string {
      const value = this.form.value as PromptDetailFormValue;
      return value.destinationExtension?.number ? value.destinationExtension?.number?.toString() : '';
   }

   getDestinationNumber(): string {
      const value = this.form.value as PromptDetailFormValue;
      return trimPhoneNumber(value.destinationNumber) ?? '';
   }

   getFile(): File | null {
      return (this.form.value as PromptDetailFormValue).fileAttachment;
   }
}
